import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';

const ARROW = '<svg viewBox="0 0 40 40" width="100%" height="100%"><circle cx="20" cy="20" r="18" fill="#0f172a" stroke="#fff" stroke-width="3"/><path d="M20 5 L29 27 L20 22 L11 27 Z" fill="#f97316" stroke="#fff" stroke-width="1.5" stroke-linejoin="round"/></svg>';

// photo shown large on the map: upright card above its position, direction arrow outside the photo at the anchor
const cardIcon = (m, id, sel) => {
  const W = Math.round(220 * ((m.ovS || 100) / 100)), ar = m.w && m.h ? m.h / m.w : 0.75, H = Math.round(W * Math.min(1.4, Math.max(0.5, ar)));
  const dir = m.dir != null ? `<span class="mc-dir" data-testid="media-card-dir-${id}" style="transform:rotate(calc(${+m.dir}deg + var(--mb, 0deg)))">${ARROW}</span>` : '<span class="mc-dot"></span>';
  return L.divIcon({ className: 'media-card-wrap', html: `<div class="media-card-box"><span class="media-card ${sel ? 'sel' : ''}" data-testid="media-card-${id}" style="width:${W}px;height:${H}px;opacity:${(m.ovO ?? 100) / 100}"><img src="${m.data}" alt=""/>${m.title || m.note ? `<b>${String(m.title || m.note).replace(/[<>&"]/g, '').slice(0, 40)}</b>` : ''}</span><i class="mc-stem"></i>${dir}</div>`, iconSize: [W, H + 46], iconAnchor: [W / 2, H + 31] });
};

// photo / video pins for media captured with GPS position
export default function useMediaLayers(mapRef, { reports = [], draft, selId, onMediaClick, onMoveMedia }) {
  const groupRef = useRef(null);
  const cb = useRef({});
  const [zoom, setZoom] = useState(null);
  cb.current = { onMediaClick, onMoveMedia };
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (!groupRef.current || groupRef.current.__map !== map) {
      groupRef.current = L.layerGroup().addTo(map);
      groupRef.current.__map = map;
      const mb = () => map.getContainer().style.setProperty('--mb', `${map.getBearing?.() || 0}deg`);
      map.on('rotate', mb); mb();
      map.on('zoomend', () => setZoom(map.getZoom())); setZoom(map.getZoom());
    }
    const g = groupRef.current;
    g.clearLayers();
    reports.forEach((r) => (r.photos || []).filter((m) => (m.lat ?? r.lat) != null && (m.lon ?? r.lon) != null).forEach((m) => {
      const sel = m.id === selId && !draft, id = String(m.id).replace(/[^\w-]/g, ''), src = m.kind === 'video' ? m.thumb : m.data;
      const big = m.ov && m.kind !== 'video' && m.data && (zoom ?? 19) >= 15;
      let icon;
      if (big) icon = cardIcon(m, id, sel);
      else {
        const img = src ? `<img src="${src}" alt=""/>` : `<em>${m.kind === 'video' ? '▶' : '◉'}</em>`;
        const arrow = m.dir != null ? `<i class="mp-dir" data-testid="media-dir-${id}" data-dir="${m.dir}" style="transform:rotate(calc(${+m.dir}deg + var(--mb, 0deg)))"></i>` : '';
        icon = L.divIcon({ className: 'media-pin-wrap', html: `<span class="media-pin ph ${m.kind === 'video' ? 'vid' : ''} ${sel ? 'sel' : ''}" data-testid="media-pin-${id}">${img}${arrow}${m.kind === 'video' && src ? '<b class="mp-play">▶</b>' : ''}</span>`, iconSize: [46, 46], iconAnchor: [23, 56] });
      }
      const mk = L.marker([m.lat ?? r.lat, m.lon ?? r.lon], { icon, draggable: sel, interactive: !draft, zIndexOffset: big ? 600 : 400 });
      mk.on('click', (e) => { L.DomEvent.stopPropagation(e); cb.current.onMediaClick?.(r.id, m.id); });
      mk.on('dragend', (e) => { const ll = e.target.getLatLng(); cb.current.onMoveMedia?.(r.id, m.id, ll.lat, ll.lng); });
      g.addLayer(mk);
    }));
  }, [mapRef, reports, draft, selId, zoom]);
}
